import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { UserAvatar } from '@/components/common/UserAvatar';
import { Globe, Crown, CreditCard, Edit, LogOut } from 'lucide-react';
import {
  getPaymentMethodDefinition,
} from '@sharecost/shared';
import type { PaymentMethodCategory, PaymentMethod } from '@sharecost/shared';
import { getPaymentMethodIcon } from '@/lib/paymentMethodIcons';
import { FREE_FOR_ALL_ENABLED } from '@/config/free-mode';
import { useTranslation } from 'react-i18next';

function formatMemberSince(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

const CATEGORY_ORDER: PaymentMethodCategory[] = [
  'bank_transfer',
  'digital_wallet',
  'mobile_payment',
];

export function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();

  const groupedPaymentMethods = useMemo(() => {
    if (!profile?.payment_methods?.length) return [];

    const groups = new Map<PaymentMethodCategory, PaymentMethod[]>();
    for (const pm of profile.payment_methods) {
      const def = getPaymentMethodDefinition(pm.type);
      const category = def?.category ?? 'digital_wallet';
      const existing = groups.get(category);
      if (existing) {
        existing.push(pm);
      } else {
        groups.set(category, [pm]);
      }
    }

    return CATEGORY_ORDER
      .filter((cat) => groups.has(cat))
      .map((cat) => ({
        category: cat,
        label: t(`profile:paymentMethods.category.${cat}`),
        methods: groups.get(cat)!,
      }));
  }, [profile?.payment_methods]);

  if (!profile) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Hero card */}
      <div className="overflow-hidden rounded-xl bg-surface-primary shadow-sm ring-1 ring-border-primary">
        <div className="h-1 bg-gradient-to-r from-brand-500 to-brand-400" />
        <div className="flex items-center gap-4 p-6">
          <UserAvatar
            name={profile.display_name}
            avatarUrl={profile.avatar_url}
            size="xl"
          />
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-2xl font-bold text-text-primary">
              {profile.display_name}
            </h1>
            <p className="truncate text-sm text-text-tertiary">
              {profile.email}
            </p>
            {profile.created_at && (
              <p className="mt-1 text-xs text-text-quaternary">
                {t('web:profilePage.memberSince')} {formatMemberSince(profile.created_at)}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => navigate('/profile/edit')}
              className="flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-hover"
            >
              <Edit className="h-4 w-4" />
              {t('profile:menu.editProfile')}
            </button>
            <button
              onClick={() => signOut()}
              className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:border-red-800/50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-4 w-4" />
              {t('common:button.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Account details */}
        <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
          <h3 className="text-lg font-semibold text-text-primary">
            {t('web:profilePage.accountDetails')}
          </h3>
          <div className="mt-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-tertiary">
                <Globe className="h-4 w-4 text-text-quaternary" />
              </div>
              <div>
                <p className="text-xs text-text-tertiary">{t('groups:createModal.field.currency')}</p>
                <p className="text-sm font-medium text-text-primary">
                  {profile.default_currency ?? 'CZK'}
                </p>
              </div>
            </div>
            {/* Subscription row is hidden during the free-for-all campaign */}
            {!FREE_FOR_ALL_ENABLED && (
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-tertiary">
                  <Crown className="h-4 w-4 text-text-quaternary" />
                </div>
                <div>
                  <p className="text-xs text-text-tertiary">{t('web:profilePage.subscription')}</p>
                  <p className="text-sm font-medium text-text-primary">
                    {profile.subscription_tier &&
                    profile.subscription_tier !== 'FREE'
                      ? t('groups:addMemberModal.premiumLabel')
                      : t('premium:status.freeTitle')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment methods */}
        <div className="rounded-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary">
          <h3 className="text-lg font-semibold text-text-primary">
            {t('web:profilePage.paymentMethods')}
          </h3>

          {groupedPaymentMethods.length > 0 ? (
            <div className="mt-5 space-y-5">
              {groupedPaymentMethods.map((group) => (
                <div key={group.category}>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-quaternary">
                    {group.label}
                  </p>
                  <ul className="space-y-2">
                    {group.methods.map((pm, i) => {
                      const def = getPaymentMethodDefinition(pm.type);
                      const Icon = getPaymentMethodIcon(
                        def?.icon ?? 'credit-card',
                      );
                      return (
                        <li
                          key={`${pm.type}-${i}`}
                          className="flex items-center gap-3 rounded-lg bg-surface-secondary px-3 py-2.5"
                        >
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-tertiary">
                            <Icon className="h-4 w-4 text-text-quaternary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-medium text-text-primary">
                                {def?.shortName ?? pm.type}
                              </p>
                              {pm.is_primary && (
                                <span className="shrink-0 rounded-full bg-brand-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-brand-600 dark:text-brand-400">
                                  {t('web:profilePage.primary')}
                                </span>
                              )}
                            </div>
                            <p className="truncate text-xs text-text-tertiary">
                              {pm.value}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-5 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border-secondary px-4 py-8">
              <CreditCard className="h-8 w-8 text-text-quaternary" />
              <p className="mt-2 text-sm text-text-tertiary">
                {t('web:profilePage.noPaymentMethodsConfigured')}
              </p>
              <p className="text-xs text-text-quaternary">
                {t('web:profilePage.addThemInTheMobileApp')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
